/**
 * Third-person camera geometry for the dungeon explorer
 * (`q-dungeon-playable-not-preview`, the camera half of (A)).
 *
 * The owner's report was "the dungeons are currently first person". They were,
 * by construction: `DungeonExplorer` put the camera AT the walker's eye
 * (`camera.position.copy(this.position)`). This module supplies what a
 * third-person boom needs INSIDE a dungeon and what the open-world orbit camera
 * never had to solve — an interior is a box, so a fixed-length boom spends most
 * of its life inside rock.
 *
 * Two rules this module exists to keep honest:
 *
 * 1. **The boom itself is not re-implemented here.** The camera pose comes from
 *    `orbitPose()` in `game/camera.ts` — the same function `OrbitCamera.update`
 *    calls — so the dungeon camera cannot drift away from the game's own rig.
 *    This file only decides HOW FAR along that boom the camera may sit.
 * 2. **`CAMERA_BOUNDS` is not widened.** Its docblock makes a bounds change an
 *    escalation, not an in-feature edit, so the explorer's user-selectable zoom
 *    is a STRICT SUB-RANGE of it (`DUNGEON_CAMERA.MIN_DIST/MAX_DIST` sit inside
 *    `CAMERA_BOUNDS.MIN_DIST/MAX_DIST`; the max is lower because no chamber
 *    has anywhere to put an 18 m boom — see `MAX_DIST`). The occlusion pull-in below is a
 *    per-frame RENDER distance, never the zoom envelope: it can only ever
 *    SHORTEN the boom the player asked for.
 *
 * ⚠ **Why the pitch range is narrow, and it is a real limit, not taste.** The
 * interior's collision is the shared sim's 2.5D footprint set (AD-044) — flat
 * x/z shapes with NO height. A footprint is therefore a truthful occluder only
 * while the camera is below the wall it stands for. Past that the boom would
 * rise over a wall the 2D test still calls solid, and the pull-in would fight a
 * wall that is no longer in front of the lens. Clamping pitch keeps the camera
 * inside the vertical band where the 2D query is valid. A genuinely free pitch
 * needs height on the collision shapes, which is a shared-sim change and its own
 * decision — not a passenger fix here.
 */

import { pushOutOfShape, type CollisionShape } from '@great-cloud/shared/client';

/**
 * The dungeon-interior camera envelope. Every distance/pitch bound here is
 * inside `CAMERA_BOUNDS` — asserted by a test, so a later edit that widens one
 * fails rather than silently escaping the game-wide envelope.
 */
export const DUNGEON_CAMERA = {
  /** Closest the PLAYER may zoom. Not the closest the camera may end up. */
  MIN_DIST: 3,
  /**
   * Furthest the player may zoom. Re-argued for the AD-404 layout (S3): chambers
   * now run from the smallest class (inscribed radius 2.5 m, ~5 m across) to boss
   * halls sized for 8–12 m patterns (R_in 12–16 m). 8 m stays: in the small class
   * the occlusion pull-in below shortens the boom to what the walls allow every
   * frame (it can never exceed the room), so a lower ceiling buys nothing there,
   * while a boss hall needs the full 8 m to frame a telegraph and the walker at
   * once. 18 m (`CAMERA_BOUNDS.MAX_DIST`) is still unusable indoors.
   */
  MAX_DIST: 8,
  MIN_PITCH: 0.15,
  /** Low ceiling: above this the boom clears walls the 2D footprints still block. */
  MAX_PITCH: 0.55,
  /** The camera's own footprint, so it stops short of a wall instead of touching it. */
  PROBE_RADIUS_M: 0.35,
  /** March resolution along the boom. Finer costs frame time for no visible gain. */
  PROBE_STEP_M: 0.25,
  /**
   * Hard floor for the pulled-in boom. When a wall is directly behind the
   * walker the camera collapses to roughly over-the-shoulder rather than into
   * the rock; it deliberately never reaches 0 (that WOULD be first person
   * again, the defect this story exists to remove).
   */
  MIN_PULLED_DIST_M: 0.9,
} as const;

/** Clamp the player's zoom into the dungeon envelope. */
export function clampDungeonDist(dist: number): number {
  return Math.min(DUNGEON_CAMERA.MAX_DIST, Math.max(DUNGEON_CAMERA.MIN_DIST, dist));
}

/** Clamp the player's pitch into the dungeon envelope. */
export function clampDungeonPitch(pitch: number): number {
  return Math.min(DUNGEON_CAMERA.MAX_PITCH, Math.max(DUNGEON_CAMERA.MIN_PITCH, pitch));
}

/** True when a probe circle at (x, z) overlaps any static shape. */
function blocked(x: number, z: number, radius: number, shapes: readonly CollisionShape[]): boolean {
  for (const shape of shapes) {
    if (pushOutOfShape(x, z, radius, shape) !== null) return true;
  }
  return false;
}

/**
 * The longest boom length ≤ `wantedDist` that keeps the camera clear of the
 * interior's static collision.
 *
 * Marches outward from the pivot in `PROBE_STEP_M` steps and returns the last
 * clear sample. Outward (not inward) is the direction that matters: it can
 * never hand back a distance with a wall BETWEEN the camera and the walker,
 * which an inward search from `wantedDist` can do whenever the walker stands in
 * a doorway and the boom re-emerges into the next room.
 *
 * Occlusion is decided with `pushOutOfShape` — the shared sim's own overlap
 * test, the same code the walker resolves against — so the camera and the walls
 * cannot disagree about where a wall is (AD-026/AD-044). Pure: `x`/`z` and the
 * shapes are all in the interior's LOCAL frame; the caller removes and reapplies
 * the group offset.
 *
 * Returns `MIN_PULLED_DIST_M` when even the first sample is blocked (walker
 * jammed into a corner), so the result is always a usable third-person length.
 *
 * `occluderTop` (AD-706, `q-dungeon-run-camera-occlusion`) gives the footprints
 * a height: the top of the tallest wall, in metres above the target's feet. The
 * boom rises monotonically, so once a sample's height (less the probe radius)
 * reaches that top, no footprint further out can stand between the lens and the
 * walker — the march stops there with the full `wantedDist`. That is what lets
 * the networked run (a roofless interior under the game's own, unclamped orbit
 * pitch) use this function without the explorer's pitch clamp: a steep boom
 * simply clears the walls. The default (`Infinity`) is the explorer's original
 * behaviour, a footprint occludes at every height.
 */
export function boomDistance(
  targetX: number,
  targetZ: number,
  yaw: number,
  pitch: number,
  wantedDist: number,
  shapes: readonly CollisionShape[],
  radius: number = DUNGEON_CAMERA.PROBE_RADIUS_M,
  step: number = DUNGEON_CAMERA.PROBE_STEP_M,
  occluderTop: number = Infinity,
): number {
  const wanted = Math.max(0, wantedDist);
  if (wanted <= DUNGEON_CAMERA.MIN_PULLED_DIST_M) return wanted;
  // The boom's horizontal run per unit of length; the vertical component does
  // not enter a 2D footprint query at all.
  const horiz = Math.cos(pitch);
  const rise = Math.sin(pitch);
  const dirX = -Math.sin(yaw) * horiz;
  const dirZ = -Math.cos(yaw) * horiz;
  let clear = DUNGEON_CAMERA.MIN_PULLED_DIST_M;
  const safeStep = step > 0 ? step : DUNGEON_CAMERA.PROBE_STEP_M;
  for (let d = DUNGEON_CAMERA.MIN_PULLED_DIST_M; d <= wanted; d += safeStep) {
    if (d * rise - radius >= occluderTop) return wanted;
    if (blocked(targetX + dirX * d, targetZ + dirZ * d, radius, shapes)) return clear;
    clear = d;
  }
  if (wanted * rise - radius >= occluderTop) return wanted;
  // The loop stops one step short of `wanted` whenever the span is not an exact
  // multiple of the step; probe the endpoint itself so an unobstructed boom
  // reaches the full length the player asked for.
  if (blocked(targetX + dirX * wanted, targetZ + dirZ * wanted, radius, shapes)) return clear;
  return wanted;
}
